import { createUnifiedTheme, palettes, UnifiedTheme } from '@backstage/theme';
import type { PageTheme } from '@backstage/theme';
import { kernColors, kernTypography, KernColors } from './kernTokens';

/**
 * Material UI does not know the KERN tokens and does arithmetic on colour values (lighten,
 * darken, alpha). MUI therefore gets the resolved hex values from kernTokens.ts, while
 * Backstage UI uses the CSS variables directly (see ../../styles/kern.css).
 */

/** KERN has no decorative gradients — page headers stay flat. */
const flatPageTheme = (background: string, fontColor: string): PageTheme => ({
  colors: [background],
  shape: 'none',
  backgroundImage: 'none',
  fontColor,
});

const pageThemes = (c: KernColors): Record<string, PageTheme> => {
  const primary = flatPageTheme(c.backgroundLevel1, c.textDefault);
  return {
    home: primary,
    documentation: primary,
    tool: primary,
    service: primary,
    website: primary,
    library: primary,
    other: primary,
    app: primary,
    apis: primary,
    card: primary,
  };
};

const kernPalette = (variant: 'light' | 'dark') => {
  const c = kernColors[variant];
  const base = variant === 'light' ? palettes.light : palettes.dark;

  return {
    ...base,
    type: variant,
    mode: variant,
    background: {
      default: c.backgroundDefault,
      paper: c.backgroundLevel1Surface,
    },
    primary: {
      main: c.actionDefault,
      contrastText: c.actionOnDefault,
    },
    secondary: {
      main: c.decorativeBrand,
      contrastText: c.actionOnDefault,
    },
    text: {
      primary: c.textDefault,
      secondary: c.textMuted,
    },
    divider: c.border,
    error: { main: c.feedbackDanger },
    warning: { main: c.feedbackWarning },
    success: { main: c.feedbackSuccess },
    info: { main: c.feedbackInfo },

    // Backstage-specific additions
    status: {
      ok: c.feedbackSuccess,
      warning: c.feedbackWarning,
      error: c.feedbackDanger,
      pending: c.feedbackInfo,
      running: c.feedbackInfo,
      aborted: c.textMuted,
    },
    border: c.border,
    textContrast: c.textDefault,
    textVerySubtle: c.borderLevel1,
    textSubtle: c.textMuted,
    highlight: c.feedbackInfoBackground,
    errorBackground: c.feedbackDangerBackground,
    warningBackground: c.feedbackWarningBackground,
    infoBackground: c.feedbackInfoBackground,
    errorText: c.feedbackDanger,
    infoText: c.feedbackInfo,
    warningText: c.feedbackWarning,
    link: c.actionDefault,
    linkHover: c.actionVisited,
    navigation: {
      background: c.backgroundLevel1,
      indicator: c.actionDefault,
      color: c.textDefault,
      selectedColor: c.actionDefault,
      navItem: {
        hoverBackground: c.backgroundLevel2,
      },
      submenu: {
        background: c.backgroundLevel1Surface,
      },
    },
    tabbar: {
      indicator: c.actionDefault,
    },
  };
};

/**
 * Gives the Material UI building blocks rendered by plugins the KERN look.
 * What Backstage renders through its own components is replaced rather than restyled
 * (see ../layout, ../nav, ../signin).
 */
const kernComponents = (variant: 'light' | 'dark') => {
  const c = kernColors[variant];
  const focus = {
    outline: `3px solid ${c.actionFocus}`,
    outlineOffset: '2px',
  };

  return {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: c.backgroundDefault,
          color: c.textDefault,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: kernTypography.borderRadius,
          textTransform: 'none' as const,
          fontWeight: 500,
          minHeight: '2.75rem',
          '&:focus-visible': focus,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
        containedPrimary: {
          backgroundColor: c.actionDefault,
          color: c.actionOnDefault,
        },
        outlined: {
          borderWidth: '2px',
          borderColor: c.actionDefault,
          color: c.actionDefault,
          '&:hover': { borderWidth: '2px' },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { '&:focus-visible': focus },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: c.actionDefault,
          textDecoration: 'underline',
          textUnderlineOffset: '0.2em',
          '&:visited': { color: c.actionVisited },
          '&:focus-visible': focus,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: c.backgroundLevel1Surface,
          backgroundImage: 'none',
        },
        rounded: { borderRadius: kernTypography.borderRadius },
        elevation1: {
          boxShadow: 'none',
          border: `1px solid ${c.borderLevel1}`,
        },
        elevation2: {
          boxShadow: 'none',
          border: `1px solid ${c.borderLevel1}`,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: kernTypography.borderRadius,
          border: `1px solid ${c.borderLevel1}`,
          boxShadow: 'none',
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        title: { fontWeight: 600 },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: kernTypography.borderRadius,
          backgroundColor: c.backgroundLevel2,
          color: c.textDefault,
          border: `1px solid ${c.borderLevel1}`,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: { backgroundColor: c.backgroundLevel1 },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${c.borderLevel1}`,
          fontSize: '1rem',
        },
        head: {
          fontWeight: 600,
          color: c.textDefault,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: c.actionDefault, height: '3px' },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none' as const,
          fontWeight: 500,
          '&:focus-visible': focus,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: c.formInputBackground,
          borderRadius: kernTypography.borderRadius,
        },
        notchedOutline: { borderColor: c.formInputBorder },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: { fontSize: '1rem' },
        input: { '&:focus-visible': focus },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: c.textDefault,
          fontWeight: 500,
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: { color: c.textMuted, fontSize: '0.875rem' },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: kernTypography.borderRadius,
          border: `1px solid ${c.borderLevel1}`,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardError: {
          backgroundColor: c.feedbackDangerBackground,
          color: c.textDefault,
        },
        standardWarning: {
          backgroundColor: c.feedbackWarningBackground,
          color: c.textDefault,
        },
        standardSuccess: {
          backgroundColor: c.feedbackSuccessBackground,
          color: c.textDefault,
        },
        standardInfo: {
          backgroundColor: c.feedbackInfoBackground,
          color: c.textDefault,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: c.backgroundInverted,
          color: c.textInverse,
          fontSize: '0.875rem',
        },
      },
    },
    BackstageHeader: {
      styleOverrides: {
        header: {
          backgroundImage: 'none',
          backgroundColor: c.backgroundLevel1,
          boxShadow: 'none',
          borderBottom: `1px solid ${c.borderLevel1}`,
        },
        title: { color: c.textDefault, fontWeight: 600 },
        subtitle: { color: c.textMuted },
        type: { color: c.textMuted },
      },
    },
    BackstageHeaderLabel: {
      styleOverrides: {
        label: { color: c.textMuted },
        value: { color: c.textDefault },
      },
    },
    BackstagePage: {
      styleOverrides: {
        root: { backgroundColor: c.backgroundDefault },
      },
    },
    BackstageContent: {
      styleOverrides: {
        root: { backgroundColor: c.backgroundDefault },
      },
    },
    BackstageInfoCard: {
      styleOverrides: {
        header: { borderBottom: `1px solid ${c.borderLevel1}` },
      },
    },
    BackstageTableHeader: {
      styleOverrides: {
        header: {
          backgroundColor: c.backgroundLevel1,
          color: c.textDefault,
          borderTop: `1px solid ${c.borderLevel1}`,
          borderBottom: `1px solid ${c.borderLevel1}`,
        },
      },
    },
    BackstageSidebar: {
      styleOverrides: {
        drawer: {
          backgroundColor: c.backgroundLevel1,
          borderRight: `1px solid ${c.borderLevel1}`,
        },
      },
    },
    BackstageSidebarItem: {
      styleOverrides: {
        root: { color: c.textDefault },
        selected: { color: c.actionDefault, borderLeftColor: c.actionDefault },
        label: { fontWeight: 500 },
      },
    },
  };
};

// Note: createUnifiedTheme calls createBaseThemeOptions itself. An additional call of our
// own would pre-fill typography and silently discard fontFamily.
const kernTheme = (variant: 'light' | 'dark'): UnifiedTheme =>
  createUnifiedTheme({
    palette: kernPalette(variant) as any,
    fontFamily: kernTypography.fontFamily,
    defaultPageTheme: 'home',
    pageTheme: pageThemes(kernColors[variant]),
    components: kernComponents(variant) as any,
  });

export const kernLightTheme = kernTheme('light');
export const kernDarkTheme = kernTheme('dark');
